import { z } from 'zod';

import {
  emailSchema,
  mobilePhoneSchema,
} from 'features/onboarding/schemas/primitives';

const timeRegex = /^([01]\d|2[0-3]):[0-5]\d$/;

const intervalSchema = z
  .object({
    open: z.string().regex(timeRegex, 'invalidTime'),
    close: z.string().regex(timeRegex, 'invalidTime'),
  })
  .refine((i) => i.open !== i.close, {
    message: 'invalidRange',
    path: ['close'],
  });

const dayHoursSchema = z
  .object({
    enabled: z.boolean(),
    mode: z.enum(['specific', '24h']),
    intervals: z.array(intervalSchema),
  })
  .superRefine((d, ctx) => {
    if (!d.enabled) return;
    if (d.mode === '24h') return;
    if (d.intervals.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'required',
        path: ['intervals'],
      });
    }
  });

export const openingHoursSchema = z
  .object({
    sunday: dayHoursSchema,
    monday: dayHoursSchema,
    tuesday: dayHoursSchema,
    wednesday: dayHoursSchema,
    thursday: dayHoursSchema,
    friday: dayHoursSchema,
    saturday: dayHoursSchema,
  })
  .refine((oh) => Object.values(oh).some((d) => d.enabled), {
    message: 'atLeastOneOpenDay',
  });

/**
 * Core business details captured on the first wizard step (Details). Address
 * and geolocation live on the following step, so they are validated
 * separately by `businessAddressSchema`.
 */
export const businessInfoCoreSchema = z.object({
  commercialName: z.string().min(1, 'required'),
  ownerName: z.string().min(1, 'required'),
  phoneNumber: mobilePhoneSchema,
  businessPhoneNumber: z.string().optional().or(z.literal('')),
  email: emailSchema,
  // Store types are hub-managed and no longer limited to the bundled list, so
  // accept any non-empty string (the value is a Pipedrive-resolvable label).
  storeType: z.string().min(1, 'required'),
  location: z.string().min(1, 'required'),
});

export type BusinessInfoCoreInput = z.infer<typeof businessInfoCoreSchema>;

/**
 * Full street address + map/GPS coordinates. Captured on the Address &
 * Questions step; the map pin is required.
 */
export const businessAddressSchema = z
  .object({
    address: z.string().min(1, 'required'),
    latitude: z.number().nullable().optional(),
    longitude: z.number().nullable().optional(),
  })
  .superRefine((v, ctx) => {
    if (typeof v.latitude !== 'number' || typeof v.longitude !== 'number') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'locationRequired',
        path: ['latitude'],
      });
    }
  });

export type BusinessAddressInput = z.infer<typeof businessAddressSchema>;

/**
 * Complete business-info schema (core + address). Used by the review step to
 * validate the whole `businessInfo` section at once.
 */
export const businessInfoSchema = businessInfoCoreSchema
  .extend({
    address: z.string().min(1, 'required'),
    latitude: z.number().nullable().optional(),
    longitude: z.number().nullable().optional(),
  })
  .superRefine((v, ctx) => {
    if (typeof v.latitude !== 'number' || typeof v.longitude !== 'number') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'locationRequired',
        path: ['latitude'],
      });
    }
  });

export type BusinessInfoInput = z.infer<typeof businessInfoSchema>;
